import { useEffect, useState } from "react";
import { Form, Input, Modal, App, Typography } from "antd";

import CustomSelect from "@/components/inputs/CustomSelect";

import {
  createKey,
  updateKey,
  createKeyCategoryRelationship,
  deleteKeyCategoryRelationship,
} from "@/api/nspd";

const { Text } = Typography;

const KEY_ORIGIN = {
  SYSTEM: "system",
  USER_DEFINED: "user_defined",
};

const SYSTEM_NSPD_KEYS = {
  GEOMETRY_POLYGON: "geometry/polygon",
  GEOMETRY_POINT: "geometry/point",
};

const SYSTEM_NSPD_KEY_OPTIONS = [
  {
    label: "Геометрия: полигон",
    value: SYSTEM_NSPD_KEYS.GEOMETRY_POLYGON,
  },
  {
    label: "Геометрия: точка",
    value: SYSTEM_NSPD_KEYS.GEOMETRY_POINT,
  },
];

const KEY_ORIGIN_OPTIONS = [
  {
    label: "Пользовательский",
    value: KEY_ORIGIN.USER_DEFINED,
  },
  {
    label: "Системный",
    value: KEY_ORIGIN.SYSTEM,
  },
];

const syncKeyCategoryRelationships = async ({
  keyUuid,
  currentCategories = [],
  nextCategoryUuids = [],
}) => {
  const currentCategoryUuids = currentCategories.map(
    (category) => category.uuid,
  );

  const categoryUuidsToCreate = nextCategoryUuids.filter(
    (categoryUuid) => !currentCategoryUuids.includes(categoryUuid),
  );

  const categoriesToDelete = currentCategories.filter(
    (category) => !nextCategoryUuids.includes(category.uuid),
  );

  for (const categoryUuid of categoryUuidsToCreate) {
    await createKeyCategoryRelationship({
      nspd_category_uuid: categoryUuid,
      nspd_key_uuid: keyUuid,
    });
  }

  for (const category of categoriesToDelete) {
    if (!category.relation_uuid) continue;

    await deleteKeyCategoryRelationship({
      uuid: category.relation_uuid,
    });
  }
};

const NSPDKeyModal = ({
  visible,
  currentKey,
  categories = [],
  onCancel,
  onSuccess,
}) => {
  const { message } = App.useApp();

  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEdit = Boolean(currentKey?.uuid);

  const nspdKeyOrigin = Form.useWatch("nspd_key_origin", form);

  const categoryOptions = categories.map((category) => {
    const currentCategory = currentKey?.categories?.find(
      (item) => item.uuid === category.uuid,
    );

    return {
      ...category,
      disabled: Boolean(currentCategory?.is_used_in_category),
    };
  });

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        name: currentKey?.name || "",
        description: currentKey?.description || "",

        nspd_key_origin: currentKey?.nspd_key_origin || KEY_ORIGIN.USER_DEFINED,

        nspd_key: currentKey?.nspd_key || "",

        category_uuids:
          currentKey?.category_uuids ||
          currentKey?.categories?.map((category) => category.uuid) ||
          [],
      });

      setError(null);
    } else {
      form.resetFields();
      setError(null);
    }
  }, [visible, currentKey, form]);

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      setIsSaving(true);
      setError(null);

      const nextCategoryUuids = values.category_uuids || [];

      const { category_uuids, ...keyPayload } = values;

      let savedKey;

      if (isEdit) {
        savedKey = await updateKey({
          ...keyPayload,
          uuid: currentKey.uuid,
        });

        await syncKeyCategoryRelationships({
          keyUuid: currentKey.uuid,
          currentCategories: currentKey?.categories || [],
          nextCategoryUuids,
        });

        message.success("Ключ обновлен");
      } else {
        savedKey = await createKey(keyPayload);

        await syncKeyCategoryRelationships({
          keyUuid: savedKey.uuid,
          currentCategories: [],
          nextCategoryUuids,
        });

        message.success("Ключ создан");
      }

      form.resetFields();
      onSuccess?.();
      onCancel();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Редактирование ключа" : "Создание ключа"}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isSaving}
      okText={isEdit ? "Сохранить" : "Создать"}
      cancelText="Отмена"
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changedValues) => {
          if ("nspd_key_origin" in changedValues) {
            form.setFieldValue("nspd_key", undefined);
          }
        }}
      >
        <Form.Item
          label="Название"
          name="name"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Введите название ключа",
            },
          ]}
        >
          <Input placeholder="Введите название ключа" />
        </Form.Item>

        <Form.Item
          label="Источник ключа НСПД"
          name="nspd_key_origin"
          rules={[
            {
              required: true,
              message: "Выберите источник ключа",
            },
          ]}
        >
          <CustomSelect
            options={KEY_ORIGIN_OPTIONS}
            placeholder="Выберите источник ключа"
            popupMatchSelectWidth={false}
          />
        </Form.Item>

        {nspdKeyOrigin === KEY_ORIGIN.SYSTEM ? (
          <Form.Item
            label="Поле запроса НСПД"
            name="nspd_key"
            rules={[
              {
                required: true,
                message: "Выберите системный ключ",
              },
            ]}
          >
            <CustomSelect
              showSearch
              allowClear
              options={SYSTEM_NSPD_KEY_OPTIONS}
              placeholder="Выберите системный ключ"
              optionFilterProp="label"
              popupMatchSelectWidth={false}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label="Поле запроса НСПД"
            name="nspd_key"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Введите название поля",
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}

        <Form.Item label="Категории" name="category_uuids">
          <CustomSelect
            mode="multiple"
            showSearch
            allowClear
            options={categoryOptions}
            fieldNames={{ label: "name", value: "uuid" }}
            optionFilterProp="name"
            placeholder="Выберите категории"
            popupMatchSelectWidth={false}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item label="Описание" name="description">
          <Input.TextArea placeholder="Введите описание" rows={4} allowClear />
        </Form.Item>

        {error && <Text type="danger">{error}</Text>}
      </Form>
    </Modal>
  );
};

export default NSPDKeyModal;
